import { Injectable } from '@angular/core';
import { DataRepository, TableRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { QueryParamsProvider } from '../../routing';
import { IonRouterOutlet, LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { LoadingIndicatorState } from '@shared';
import {
	AlertService,
	ToastService,
	ToastDuration,
	ErrorCodes,
	TableModel,
	DataModel,
	DeviceActionsService,
	TableTypeEnum,
	PrimaryKeyValueModel,
	mapPrimaryKeysArrayToObject,
	delayExecution
} from '@shared';
import { LoggingService } from '@shared/logging';
import { Subject, firstValueFrom, Observable, combineLatest, from, of, iif } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { BusinessLayerService } from '../services/business-layer.service';
import { TableTypeProvider } from '../services/table-type.provider';
import { DeleteChainParams, IValidationsRule } from './delete-validations/handlers/models';
import { DeleteValidatorsOrchestratorService } from './delete-validations/services';
import { ViewConfigurationService } from 'src/domain/view-configuration';

@Injectable()
export class EntryBllService {
	public constructor(
		private dataRepository: DataRepository,
		private alertService: AlertService,
		private translateService: TranslateService,
		private toastService: ToastService,
		private logger: LoggingService,
		private deleteValidatorsOrchestrator: DeleteValidatorsOrchestratorService,
		private queryParamsProvider: QueryParamsProvider,
		private tableRepository: TableRepository,
		private viewConfigurationService: ViewConfigurationService,
		private loadingIndicator: LoadingIndicatorState,
		private deviceActionsService: DeviceActionsService,
		private tableTypeService: TableTypeProvider,
		private businessLayerService: BusinessLayerService,
		private loadingController: LoadingController
	) {}

	public async deleteEntry(tableName: string, entryId: number, routerOutlet: IonRouterOutlet): Promise<boolean> {
		const alertSubject = new Subject<boolean>();
		let isModalOpened = false;
		let recordSuccessfullyDeleted = false;
		let hasChildProperties = false;

		hasChildProperties = await this.parentHasChildRecords(tableName, entryId);

		if (!isModalOpened) {
			const cancelButton = {
				text: this.translateService.instant('alert.buttons.keepRecord'),
				handler: () => {
					isModalOpened = false;
					alertSubject.next(false);
				}
			};

			const confirmButton = {
				text: this.translateService.instant('alert.buttons.deleteRecord'),
				handler: () => {
					alertSubject.next(true);
					isModalOpened = false;
				}
			};
			isModalOpened = true;
			this.alertService.presentAlert(
				hasChildProperties
					? this.translateService.instant('alert.deleteParentWithChildrensMessage')
					: this.translateService.instant('alert.deleteMessage'),
				[cancelButton, confirmButton],
				false
			);

			this.deviceActionsService.deactivateBackButton(routerOutlet);
			const userChoice = await firstValueFrom(alertSubject);

			try {
				if (userChoice) {
					this.loadingIndicator.setIsLoading(
						true,
						this.translateService.instant('loadingIndicator.deleteMessage')
					);
					const validationTriggered = await this.validateDelete(tableName, entryId);

					if (validationTriggered) {
						return await this.toastService.presentError(
							this.translateService.instant(
								`alert.deleteValidations.${validationTriggered.validationKey}`
							),
							ToastDuration.Fast
						);
					}

					recordSuccessfullyDeleted = await this.deleteRecord(
						this.queryParamsProvider.workfileId,
						tableName,
						entryId
					);

					await this.toastService.presentSuccess(
						this.translateService.instant('form.mesages.successDelete'),
						ToastDuration.Fast
					);
				}
			} catch (error) {
				this.logger.error(ErrorCodes.DeleteRecordFailed, error);
				throw error;
			} finally {
				this.loadingIndicator.setIsLoading(false);
				this.deviceActionsService.reactivateBackButton(routerOutlet);
			}
		}

		return recordSuccessfullyDeleted;
	}

	public async deleteRecord(workfileId: string, tableName: string, entryId: number): Promise<boolean> {
		const data = await firstValueFrom(this.dataRepository.getTableDataById$(tableName, entryId));
		const holeNumber = data.hole_number;
		let errorDetailsChild: { child?: Record<string, number>[] } | null = null;

		if (!errorDetailsChild) {
			errorDetailsChild = { child: [] };
		}
		
		const isParentTable = await firstValueFrom(
			this.tableTypeService.isParentTable$(workfileId, tableName).pipe(map((isParent: boolean) => isParent))
		);

		if (isParentTable) {
			await this.dataRepository.updateOne({ isDeleted: true }, tableName, entryId, []);
			this.viewConfigurationService.deleteErrorDetail(workfileId, holeNumber)
			return true;
		} else {
			await this.dataRepository.delete(tableName, entryId);
			errorDetailsChild.child.push({ [tableName]: -1 });
			this.viewConfigurationService.setErrorDetail(workfileId, holeNumber, errorDetailsChild)
			return true
		}
	}

	private async parentHasChildRecords(tableName: string, entryId: number): Promise<boolean> {
		const tableType = await firstValueFrom(
			this.tableTypeService
				.getTableType$(tableName, this.queryParamsProvider.workfileId)
				.pipe(map((tableTypes: string[]) => tableTypes))
		);

		if (
			!tableType.includes(TableTypeEnum.parentTable) &&
			(tableType.includes(TableTypeEnum.flatTable) || tableType.includes(TableTypeEnum.flatSampleTable))
		) {
			return false;
		}

		this.loadingIndicator.setIsLoading(true, this.translateService.instant('loadingIndicator.deleteMessage'));
		// todo: loading indicator has to stay visible a bit more
		// otherwise it looks like a bug or something, find a better way to handle this scenario
		await delayExecution(500);

		const hasChildRecords = await firstValueFrom(
			this.tableRepository.getTableChildIdsByName$(tableName).pipe(
				switchMap((childTableIds: number[] | null) =>
					iif(
						() => !!childTableIds,
						this.businessLayerService
							.getPrimaryKeyAndValue$(
								this.queryParamsProvider.workfileId,
								this.queryParamsProvider.projectNumber,
								tableName,
								entryId
							)
							.pipe(
								switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
									this.tableRepository.getChildTablesMetadataByIds$(childTableIds).pipe(
										switchMap((childTables: TableModel[]) => {
											const childTablesName = childTables.map((childTable: TableModel) =>
												childTable.table_name.toLowerCase()
											);
											return this.dataRepository
												.getRecordsTotalCount$(
													childTablesName,
													mapPrimaryKeysArrayToObject(primaryKeyValue)
												)
												.pipe(
													switchMap((recordsTotalCount: number) => of(recordsTotalCount > 0))
												);
										})
									)
								)
							),
						of(false)
					)
				)
			)
		);

		this.loadingIndicator.setIsLoading(false);
		// todo: loading indicator has to stay visible a bit more
		// otherwise it looks like a bug or something, find a better way to handle this scenario
		await delayExecution(500);
		await this.closeLoader();

		return hasChildRecords;
	}

	private async validateDelete(tableName: string, entryId: number): Promise<IValidationsRule> {
		return await firstValueFrom(
			this.getEntryMetadata$(tableName, entryId).pipe(
				map((sharedData: { tableMetadata: TableModel; tableData: DataModel }) => {
					const deleteChainParams: DeleteChainParams = {
						tableMetadata: sharedData.tableMetadata,
						depthFrom:
							WorkspaceDatabaseKeys.columns.depthFrom in sharedData.tableData
								? Number(sharedData.tableData.depth_from)
								: null,
						depthTo:
							WorkspaceDatabaseKeys.columns.depthTo in sharedData.tableData
								? Number(sharedData.tableData.depth_to)
								: null,
						sampleType:
							WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode in sharedData.tableData
								? sharedData.tableData.assay_sample_type_code
								: null,
						tableName,
						entityName: this.queryParamsProvider.parentTableName ?? '',
						projectNumber: this.queryParamsProvider.projectNumber ?? '',
						entryId: this.queryParamsProvider.entryId ?? '',
						childEntryId: entryId ?? 0,
						workfileId: this.queryParamsProvider.workfileId ?? ''
					};
					return deleteChainParams;
				}),
				switchMap((deleteChainParams: DeleteChainParams) =>
					from(this.deleteValidatorsOrchestrator.startProcess(deleteChainParams))
				)
			)
		);
	}
	private getTableMetadata$(tableName: string): Observable<TableModel> {
		return this.tableRepository.getTableMetadata$(tableName).pipe(
			first(),
			catchError((error) =>
				this.toastService.presentError(`${error}: Fetching table metadata failed`, ToastDuration.Slow)
			)
		);
	}

	private getEntryMetadata$(
		tableName: string,
		entryId: number
	): Observable<{ tableMetadata: TableModel; tableData: DataModel }> {
		return combineLatest([
			this.getTableMetadata$(tableName),
			this.dataRepository
				.getTableEntry$(tableName, this.queryParamsProvider.projectNumber, entryId)
				.pipe(map((tableData: DataModel) => (tableData ? tableData : null)))
		]).pipe(
			map((sharedData: [tableMetadata: TableModel, tableData: DataModel]) => ({
				tableMetadata: sharedData[0],
				tableData: sharedData[1]
			}))
		);
	}

	private async closeLoader() {
		await this.checkAndCloseLoader();
		setTimeout(() => this.checkAndCloseLoader(), 500);
	}

	private async checkAndCloseLoader() {
		const loader = await this.loadingController.getTop();
		if (loader !== undefined) {
			await this.loadingController.dismiss();
		}
	}
}
